/**
 * DocumentRow Component
 *
 * Displays a single document item with filename, type, size, date, and download link.
 * Used in the DocumentsPage list view.
 *
 * Requirements: Task 10 - Documents Page
 */

import { Card, CardContent, Badge, Button } from '@bloomcode/ui';
import { FileText, Download, Calendar, HardDrive } from '@bloomcode/icons';
import type { GetMyDocumentsQuery } from '@bloomcode/graphql';

type DocumentItem = GetMyDocumentsQuery['getMyDocuments']['data'][number];

interface DocumentRowProps {
  document: DocumentItem;
}

/**
 * Formats file size to human-readable format
 */
function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Formats a date string to a readable format
 */
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Unknown date';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Returns a badge variant based on communication type
 */
function getTypeBadgeVariant(type: string | null | undefined): 'default' | 'secondary' | 'outline' {
  switch (type?.toUpperCase()) {
    case 'EMAIL':
      return 'default';
    case 'NOTIFICATION':
      return 'secondary';
    default:
      return 'outline';
  }
}

export function DocumentRow({ document }: DocumentRowProps) {
  const handleDownload = () => {
    if (document.downloadUrl) {
      window.open(document.downloadUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Card className="transition-all hover:shadow-sm hover:border-primary/20">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          {/* File icon */}
          <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <FileText className="h-5 w-5 text-primary" />
          </div>

          {/* File info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-foreground truncate">{document.filename}</h3>
            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
              {document.communicationTitle && (
                <span className="truncate max-w-[200px]">{document.communicationTitle}</span>
              )}
              {document.type && (
                <Badge variant={getTypeBadgeVariant(document.communicationType)}>
                  {document.type}
                </Badge>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="hidden sm:flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <HardDrive className="h-3.5 w-3.5" />
              {formatFileSize(document.size)}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {formatDate(document.uploadDate)}
            </span>
          </div>

          {/* Download button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={!document.downloadUrl}
            aria-label={`Download ${document.filename}`}
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>

        {/* Mobile metadata */}
        <div className="flex sm:hidden items-center gap-3 mt-2 text-xs text-muted-foreground pl-14">
          <span className="flex items-center gap-1">
            <HardDrive className="h-3 w-3" />
            {formatFileSize(document.size)}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {formatDate(document.uploadDate)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export default DocumentRow;
